import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { usersAPI, clientsAPI, entitiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Plus, Search, Edit, Trash2, MoreHorizontal, Building2, Mail, RotateCcw, AlertTriangle } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ROLES = [
  { value: 'dpo', label: 'DPO', description: 'Full Access', color: 'default', needsClient: false, needsEntity: false },
  { value: 'client_admin', label: 'Client Admin', description: 'Full access to client', color: 'secondary', needsClient: true, needsEntity: false },
  { value: 'client_viewer', label: 'Client Viewer', description: 'Read-only client access', color: 'outline', needsClient: true, needsEntity: false },
  { value: 'entity_admin', label: 'Entity Admin', description: 'Full access to entity', color: 'secondary', needsClient: true, needsEntity: true },
  { value: 'entity_viewer', label: 'Entity Viewer', description: 'Read-only entity access', color: 'outline', needsClient: true, needsEntity: true },
];

// Task 4: Role assignment hierarchy - which roles can assign which roles (Sprint 1 spec section 3.3)
const CAN_ASSIGN_ROLE = {
  super_admin: ['super_admin', 'dpo', 'client_admin', 'client_viewer', 'entity_admin', 'entity_viewer'],
  dpo: ['client_admin', 'client_viewer', 'entity_admin', 'entity_viewer'],
  client_admin: ['client_viewer', 'entity_admin', 'entity_viewer'],
  entity_admin: ['entity_viewer'],
  client_viewer: [],
  entity_viewer: [],
};

// Helper: Get list of assignable roles for current user
const getAssignableRoles = (userRole) => {
  return CAN_ASSIGN_ROLE[userRole] || [];
};

export default function UserManagementPage() {
  const { isDPO, isEntityAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  const [allEntities, setAllEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seatInfo, setSeatInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'entity_viewer',
    client_id: '',
    entity_id: '',
    is_active: true
  });
  const [inviteSending, setInviteSending] = useState(false);

  // Task 3: Pre-fill form for entity_admin (auto-set client_id and entity_id)
  useEffect(() => {
    if (currentUser?.role === 'entity_admin' && currentUser?.client_id) {
      setFormData(prev => ({
        ...prev,
        client_id: currentUser.client_id,
        entity_id: currentUser.entity_id || ''
      }));
    }
  }, [currentUser?.role]);

  useEffect(() => {
    fetchData();
  }, [selectedClient]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const requests = [
        usersAPI.getAll(selectedClient || undefined),
        isDPO ? clientsAPI.getAll() : Promise.resolve({ data: [] }),
        isDPO ? entitiesAPI.getAll() : Promise.resolve({ data: [] }),
        // Task 5B: Fetch per-client seat info (if client selected)
        selectedClient ? clientsAPI.getSeats(selectedClient) : Promise.resolve(null)
      ];
      const [usersRes, clientsRes, entitiesRes, seatRes] = await Promise.all(requests);
      setUsers(usersRes.data);
      if (isDPO) {
        setClients(clientsRes.data);
        setAllEntities(entitiesRes.data);
        // Task 5B: Set seat info from selected client (per-client, not DPO-org wide)
        if (seatRes?.data) {
          const seatData = seatRes.data;
          setSeatInfo({
            used: seatData.user_count || 0,
            max: seatData.seat_limit || 3,
            percent: seatData.percent_used || 0,
            is_at_limit: seatData.is_at_limit || false,
            subscription_tier: seatData.subscription_tier
          });
        } else {
          // No client selected yet
          setSeatInfo(null);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch entities when client changes in form
  useEffect(() => {
    if (formData.client_id) {
      const clientEntities = allEntities.filter(e => e.client_id === formData.client_id);
      setEntities(clientEntities);
    } else {
      setEntities([]);
    }
  }, [formData.client_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedRole = ROLES.find(r => r.value === formData.role);
    
    // Validation
    if (selectedRole?.needsClient && !formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (selectedRole?.needsEntity && !formData.entity_id) {
      toast.error('Please select an entity');
      return;
    }

    setInviteSending(true);
    try {
      if (editing) {
        // Prepare non-email fields for normal update
        const userData = {
          full_name: formData.full_name,
          role: formData.role,
          is_active: formData.is_active,
        };
        if (selectedRole?.needsClient) userData.client_id = formData.client_id;
        if (selectedRole?.needsEntity) userData.entity_id = formData.entity_id;

        await usersAPI.update(editing.id, userData);

        // Handle email change separately — it triggers a verification flow
        const emailChanged = formData.email.trim().toLowerCase() !== editing.email.toLowerCase();
        if (emailChanged) {
          await usersAPI.requestEmailChange(editing.id, formData.email.trim());
          toast.success(
            `User updated. Email change initiated — a verification link has been sent to ${formData.email}. The account is locked until verified.`,
            { duration: 6000 }
          );
        } else {
          toast.success('User updated');
        }
      } else {
        // Invite new user
        const userData = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          is_active: formData.is_active,
        };
        if (selectedRole?.needsClient) userData.client_id = formData.client_id;
        if (selectedRole?.needsEntity) userData.entity_id = formData.entity_id;
        await usersAPI.inviteUser(userData);
        toast.success('Invitation sent! User will receive email with setup link.');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setInviteSending(false);
    }
  };

  const handleEdit = (user) => {
    setEditing(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      client_id: user.client_id || '',
      entity_id: user.entity_id || '',
      is_active: user.is_active,
      password: ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error('Cannot delete yourself');
      return;
    }
    if (!window.confirm('Delete this user?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      await usersAPI.resendInvite(userId);
      toast.success('Invitation resent! User will receive email with setup link.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      email: '',
      full_name: '',
      role: 'entity_viewer',
      client_id: '',
      entity_id: '',
      is_active: true
    });
  };

  const handleRoleChange = (value) => {
    const role = ROLES.find(r => r.value === value);
    setFormData(prev => ({
      ...prev,
      role: value,
      client_id: role?.needsClient ? prev.client_id : '',
      entity_id: role?.needsEntity ? prev.entity_id : ''
    }));
  };

  const handleClientChange = (value) => {
    setFormData(prev => ({
      ...prev,
      client_id: value,
      entity_id: '' // Reset entity when client changes
    }));
  };

  const filteredUsers = users.filter(u =>
    u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.client_name || '-';
  const getEntityName = (entityId) => allEntities.find(e => e.id === entityId)?.entity_name || '-';

  const selectedRole = ROLES.find(r => r.value === formData.role);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="users-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">User Management</h1>
            <p className="text-muted-foreground">Manage system users and access</p>
          </div>
          <div className="flex items-center gap-3">
            {isDPO && seatInfo && selectedClient && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Seats:</span>
                <span className={`text-sm px-3 py-1 rounded-md border ${
                  seatInfo.is_at_limit
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : seatInfo.percent >= 80
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}>
                  {seatInfo.used} / {seatInfo.max} used
                </span>
              </div>
            )}
            {isDPO && !selectedClient && (
              <span className="text-xs text-muted-foreground italic">Select a client to view seat usage</span>
            )}
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
          </div>
          {isDPO && (
            <Select value={selectedClient || "all"} onValueChange={(val) => setSelectedClient(val === "all" ? "" : val)}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(client => (<SelectItem key={client.id} value={client.id}>{client.client_name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  {isDPO && <TableHead>Client</TableHead>}
                  {isDPO && <TableHead>Entity</TableHead>}
                  <TableHead>Account Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isDPO ? 7 : 5} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={isDPO ? 7 : 5} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const role = ROLES.find(r => r.value === user.role);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{user.full_name}</p>
                              {user.id === currentUser?.id && (
                                <Badge variant="secondary" className="text-xs">You</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={role?.color || 'outline'}>{role?.label || user.role}</Badge>
                        </TableCell>
                        {isDPO && <TableCell>{getClientName(user.client_id)}</TableCell>}
                        {isDPO && <TableCell>{getEntityName(user.entity_id)}</TableCell>}
                        <TableCell>
                          {!user.is_active ? (
                            <Badge variant="secondary">Inactive</Badge>
                          ) : user.status === 'pending' ? (
                            <Badge className="bg-amber-100 text-amber-900 border border-amber-300">Pending Setup</Badge>
                          ) : user.status === 'suspended' ? (
                            <Badge variant="secondary">Suspended</Badge>
                          ) : (
                            <Badge variant="success">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell>{format(new Date(user.created_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          {user.id === currentUser?.id ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title="You cannot manage your own account"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(user)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResendInvite(user.id)}><RotateCcw className="h-4 w-4 mr-2" />Resend Invitation</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(user.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit User' : 'Invite User'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Manage user account and access permissions' : 'Send invitation email with account setup link'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              {editing?.email_change_pending && (
                <div className="flex items-start gap-2 mx-0 mb-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Email change pending</p>
                    <p className="text-xs mt-0.5">
                      Awaiting verification of <strong>{editing.pending_email}</strong>.
                      Account is locked. Setting a new email here will replace the pending change and resend verification.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input value={formData.full_name} onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))} required data-testid="user-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                    {editing && formData.email.trim().toLowerCase() !== editing.email.toLowerCase() && (
                      <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          Changing email will lock this account until the user verifies the new address.
                          Notification emails will be sent to both addresses.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={handleRoleChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* Task 4: Only show roles that current user can assign (Sprint 1 spec section 3.3) */}
                      {ROLES.map(role => {
                        const canAssign = getAssignableRoles(currentUser?.role).includes(role.value);
                        return (
                          <SelectItem key={role.value} value={role.value} disabled={!canAssign}>
                            <div className="flex items-center gap-2">
                              <span>{role.label}</span>
                              <span className="text-sm text-muted-foreground">- {role.description}</span>
                              {!canAssign && <span className="text-xs text-red-600">(Not available)</span>}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Client Selection (Task 3: Hidden for entity_admin, shown as info for others) */}
                {selectedRole?.needsClient && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Client *
                    </Label>
                    {isEntityAdmin ? (
                      // Entity Admin: show their client as disabled Select (read-only, dark background)
                      <Select value={formData.client_id} disabled>
                        <SelectTrigger data-testid="user-client-select" disabled>
                          <SelectValue placeholder="Your client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.client_name} ({client.client_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      // DPO / Client Admin: show dropdown
                      <Select value={formData.client_id} onValueChange={handleClientChange}>
                        <SelectTrigger data-testid="user-client-select">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.client_name} ({client.client_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Entity Selection (Task 3: Hidden for entity_admin, shown as info for others) */}
                {selectedRole?.needsEntity && formData.client_id && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Entity *
                    </Label>
                    {isEntityAdmin ? (
                      // Entity Admin: show their entity as disabled Select (read-only, dark background)
                      <Select value={formData.entity_id} disabled>
                        <SelectTrigger data-testid="user-entity-select" disabled>
                          <SelectValue placeholder="Your entity" />
                        </SelectTrigger>
                        <SelectContent>
                          {allEntities.map(entity => (
                            <SelectItem key={entity.id} value={entity.id}>
                              {entity.entity_name} ({entity.entity_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      // DPO / Client Admin: show dropdown
                      <Select value={formData.entity_id} onValueChange={(value) => setFormData(prev => ({ ...prev, entity_id: value }))}>
                        <SelectTrigger data-testid="user-entity-select">
                          <SelectValue placeholder="Select entity" />
                        </SelectTrigger>
                        <SelectContent>
                          {entities.map(entity => (
                            <SelectItem key={entity.id} value={entity.id}>
                              {entity.entity_name} ({entity.entity_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {!isEntityAdmin && entities.length === 0 && (
                      <p className="text-xs text-amber-600">No entities found for this client.</p>
                    )}
                  </div>
                )}

                {editing && (
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.is_active ? 'active' : 'inactive'} onValueChange={(value) => setFormData(prev => ({ ...prev, is_active: value === 'active' }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={inviteSending} data-testid="save-user-btn">
                  {inviteSending
                    ? (editing ? 'Updating...' : 'Sending...')
                    : (editing ? 'Update' : 'Send Invitation')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
